// Live handling of `admin_order_updated:{order_id}` delay-timeline snapshots.
//
// The server sends a calculated snapshot (`data.delay_timeline.steps`, matched
// by `key`) stamped with `data.calculated_at`. Between snapshots the client
// advances the elapsed values of the *active* steps once a minute, using the
// server clock (corrected for drift) instead of the local one.
//
// Step fields read here (adjust if the API names differ):
//   is_active        -> step is currently running
//   elapsed_minutes  -> minutes elapsed on that step at `calculated_at`
//   allowed_minutes  -> allowed minutes for the step
//   delay_minutes    -> max(0, elapsed - allowed)

export const FINAL_STATUSES = [
  "driver_finished_delivery",
  "admin_delivered",
  "client_cancel",
  "driver_cancel",
  "restaurant_reject",
  "restaurant_cancel",
  "admin_cancel",
  "admin_rejected",
  "driver_rejected",
  "admin_returned",
];

// Steps that are not time-based and must never be advanced locally.
const NON_TIMED_KEYS = ["order_delay_percentage"];

// The support step's `button_color` reports receipt (red = not received,
// green = received), never lateness; read lateness from `isStepDelayed`.
export const SUPPORT_STEP_KEY = "support_received";

export const findSupportStep = (order) =>
  (order &&
    order.delay_timeline &&
    Array.isArray(order.delay_timeline.steps) &&
    order.delay_timeline.steps.find((s) => s && s.key === SUPPORT_STEP_KEY)) ||
  null;

/** Lateness of a step, independent of its receipt/button state. */
export const isStepDelayed = (step) =>
  !!(
    step &&
    (step.is_delayed || step.is_overdue || Number(step.delay_minutes) > 0)
  );

export const isFinalStatus = (status) => FINAL_STATUSES.includes(status);

export const TICK_MS = 60 * 1000;
export const COUNTDOWN_MS = 1000;
/**
 * A timed step is running while it has started, is not finished and the
 * order was not cancelled. Falls back to `is_active` for legacy payloads.
 */
export const isStepRunning = (step) => {
  if (!step) return false;
  if (step.is_completed || step.is_cancelled || step.is_received) return false;
  if (step.is_applicable === false) return false;
  if (step.is_applicable === undefined && step.is_active !== undefined) {
    return !!step.is_active;
  }
  return true;
};

/** Format seconds as MM:SS. */
export const formatCountdown = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

/**
 * Cast a UTC "YYYY-MM-DD HH:mm:ss" value (no zone marker) to the browser's
 * local time zone, as "YYYY-MM-DD HH:mm:ss". Unparseable values pass through.
 */
export const utcToLocal = (value) => {
  if (!value) return "";
  const text = String(value);
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(text);
  const date = new Date(hasZone ? text : text.replace(" ", "T") + "Z");
  if (Number.isNaN(date.getTime())) return text;
  const p = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ` +
    `${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`
  );
};

/**
 * Text for a delay step on the line, following the countdown contract.
 * e.g. "المسموح: 2 د | المتبقي: 01:30"
 */
export const describeStep = (step) => {
  if (!step) return "";
  if (step.is_cancelled) return "أغلقت بسبب إلغاء الطلب";
  if (step.is_applicable === false) return "—";
  const allowed = `المسموح: ${step.allowed_minutes} د`;
  if (step.is_completed) {
    const endedAt = step.ended_at_local || utcToLocal(step.ended_at);
    const end = endedAt ? ` | انتهت: ${endedAt}` : "";
    const delay = Number(step.delay_minutes) || 0;
    return `${allowed} | التأخير: ${delay} د${end}`;
  }
  if (step.is_overdue || Number(step.delay_minutes) > 0) {
    return `${allowed} | التأخير: ${Number(step.delay_minutes) || 0} د`;
  }
  if (step.remaining_seconds !== undefined && step.remaining_seconds !== null) {
    return `${allowed} | المتبقي: ${formatCountdown(step.remaining_seconds)}`;
  }
  return `${allowed} | التأخير: ${Number(step.delay_minutes) || 0} د`;
};

/** Red when overdue, green otherwise (cancelled/not started stay neutral-green). */
export const stepColor = (step) => {
  if (!step) return "green";
  if (step.is_cancelled || step.is_applicable === false) return "green";
  if (step.is_overdue || Number(step.delay_minutes) > 0) return "red";
  return step.button_color === "red" ? "red" : "green";
};

const toMs = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const ms = typeof value === "number" ? value : Date.parse(value);
  if (Number.isNaN(ms)) return null;
  // Allow unix seconds as well as milliseconds.
  return ms < 1e11 ? ms * 1000 : ms;
};

/**
 * Tracks the server clock offset and the per-order base snapshot.
 * Keep one instance per component (not reactive).
 */
export function createLiveTimeline() {
  let clockOffset = null; // client now - server now, in ms
  const bases = new Map(); // order id -> { at, total, steps: { key: {elapsed, delay} } }
  const firedExpiry = new Set(); // deadlines already reported by tickCountdown

  const serverNow = () => Date.now() - (clockOffset || 0);

  const observeClock = (serverMs) => {
    const offset = Date.now() - serverMs;
    // The smallest offset is the one with the least network latency.
    if (clockOffset === null || offset < clockOffset) clockOffset = offset;
  };

  const readBase = (order, atMs) => {
    const timeline = order.delay_timeline;
    const steps = {};
    (timeline.steps || []).forEach((s) => {
      if (!s || !s.key) return;
      const delay = Number(s.delay_minutes) || 0;
      const allowed = Number(s.allowed_minutes) || 0;
      let elapsed = Number(s.elapsed_minutes);
      if (Number.isNaN(elapsed)) elapsed = delay > 0 ? allowed + delay : null;
      const remaining = Number(s.remaining_seconds);
      steps[s.key] = {
        elapsed,
        delay,
        remaining: Number.isNaN(remaining) ? null : remaining,
      };
    });
    bases.set(order.id, {
      at: atMs,
      total: Number(timeline.total_delay_minutes) || 0,
      steps,
    });
  };

  /** True when a payload carries a usable server snapshot. */
  const isCompletePayload = (data) =>
    !!(
      data &&
      data.delay_timeline &&
      Array.isArray(data.delay_timeline.steps) &&
      data.delay_timeline.steps.length &&
      toMs(data.calculated_at) !== null
    );

  /**
   * Merge a server snapshot into `order` (matching steps by `key`).
   * Returns false when the payload is incomplete and the order should be
   * re-fetched instead.
   */
  const applySnapshot = (vm, order, data) => {
    if (!order || !data) return false;
    const complete = isCompletePayload(data);
    const incoming = complete
      ? data.delay_timeline.steps
      : Array.isArray(data.steps)
      ? data.steps // legacy payload
      : null;
    if (!incoming) return false;

    if (!order.delay_timeline) vm.$set(order, "delay_timeline", { steps: [] });
    const timeline = order.delay_timeline;
    if (!Array.isArray(timeline.steps)) vm.$set(timeline, "steps", []);

    incoming.forEach((next) => {
      if (!next || !next.key) return;
      const idx = timeline.steps.findIndex((s) => s.key === next.key);
      if (idx === -1) {
        timeline.steps.push({ ...next });
      } else {
        Object.keys(next).forEach((k) => vm.$set(timeline.steps[idx], k, next[k]));
      }
      if (next.key === "support_received") {
        if (next.support_employee_name !== undefined) {
          vm.$set(order, "support_employee_name", next.support_employee_name);
        }
        if (next.received_at !== undefined) {
          vm.$set(order, "support_received_at", next.received_at);
        }
      }
    });

    if (complete) {
      ["total_delay_minutes", "delay_percentage"].forEach((k) => {
        if (data.delay_timeline[k] !== undefined) {
          vm.$set(timeline, k, data.delay_timeline[k]);
        }
      });
      if (data.status !== undefined) vm.$set(order, "status", data.status);
      if (data.status_trans !== undefined) {
        vm.$set(order, "status_trans", data.status_trans);
      }
      const serverMs = toMs(data.calculated_at);
      observeClock(serverMs);
      readBase(order, serverMs);
    }
    return complete;
  };

  /** Advance the active steps of `order` to the current server time. */
  const tickOrder = (vm, order) => {
    if (!order || !order.delay_timeline || isFinalStatus(order.status)) return;
    const timeline = order.delay_timeline;
    if (!Array.isArray(timeline.steps)) return;
    if (!bases.has(order.id)) readBase(order, serverNow());
    const base = bases.get(order.id);
    const extra = Math.max(0, Math.floor((serverNow() - base.at) / TICK_MS));

    let delayChange = 0;
    timeline.steps.forEach((step) => {
      if (!isStepRunning(step) || NON_TIMED_KEYS.includes(step.key)) return;
      const b = base.steps[step.key];
      if (!b || b.elapsed === null) return;
      const elapsed = b.elapsed + extra;
      const delay = Math.max(0, elapsed - (Number(step.allowed_minutes) || 0));
      delayChange += delay - b.delay;
      if (step.elapsed_minutes !== undefined) {
        vm.$set(step, "elapsed_minutes", elapsed);
      }
      vm.$set(step, "delay_minutes", delay);
      if (delay > 0 && step.key !== SUPPORT_STEP_KEY) {
        vm.$set(step, "button_color", "red");
      }
    });
    if (delayChange !== 0 || extra > 0) {
      vm.$set(timeline, "total_delay_minutes", base.total + delayChange);
    }
  };

  /**
   * Once-per-second display countdown. Starts from the server's
   * `remaining_seconds` and never restarts from `allowed_minutes`; at zero it
   * stops and waits for the next API/socket update.
   */
  const tickCountdown = (vm, order) => {
    if (!order || !order.delay_timeline || isFinalStatus(order.status)) return;
    const steps = order.delay_timeline.steps;
    if (!Array.isArray(steps)) return;
    if (!bases.has(order.id)) readBase(order, serverNow());
    const base = bases.get(order.id);
    const passed = Math.max(0, Math.floor((serverNow() - base.at) / 1000));
    let expired = false;
    steps.forEach((step) => {
      if (!isStepRunning(step) || NON_TIMED_KEYS.includes(step.key)) return;
      const b = base.steps[step.key];
      if (!b || b.remaining === null) return;
      const remaining = Math.max(0, b.remaining - passed);
      if (step.remaining_seconds !== remaining) {
        if (remaining === 0 && Number(step.remaining_seconds) > 0) {
          // Report each deadline once, even if the refetch still looks stale.
          const key = `${order.id}:${step.key}:${step.finish_at}`;
          if (!firedExpiry.has(key)) {
            firedExpiry.add(key);
            expired = true;
          }
        }
        vm.$set(step, "remaining_seconds", remaining);
      }
    });
    return expired;
  };

  const forget = (id) => bases.delete(id);
  const reset = () => bases.clear();

  return {
    applySnapshot,
    tickOrder,
    tickCountdown,
    isCompletePayload,
    forget,
    reset,
  };
}
